import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, X, Copy, Check, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  binder,
  externalGrants,
  studies,
  isBlindedStudyType,
  type RemoteAccessRole,
  type RemoteAccessScope,
  type CreateExternalGrantResponse,
} from '@/api/client';
import { binderScope } from '@/lib/binderScope';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-11 — Coordinator dialog: issue a new external-access grant on a
 * binder. Mirrors the existing eISF approval-initiate dialog (NewDocumentSlotDialog
 * style) for visual consistency: fixed overlay, card form, header X-button,
 * label/input rows, btn-ghost + btn-primary footer.
 *
 * Flow:
 *   1. Coordinator fills name + email + role (sponsor_monitor | auditor) +
 *      scope (view_full_binder | view_pii_redacted | view_specific_folders)
 *      + valid_from / valid_to.
 *   2. POST /binders/:id/external/grants -> { grant_id, magic_link_url }.
 *   3. Backend emails the magic link directly. We ALSO show the URL inline
 *      with a copy-to-clipboard button so the coordinator can forward it
 *      manually if the email is bounced or quarantined.
 *   4. "Done" closes the dialog and invalidates the parent list query.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SCOPE_OPTIONS: Array<{ value: RemoteAccessScope; label: string; description: string }> = [
  {
    value: 'view_full_binder',
    label: 'View full binder',
    description: 'Reviewer sees every folder + every version including PHI.',
  },
  {
    value: 'view_pii_redacted',
    label: 'View binder (PII redacted)',
    description: 'Signer identity + free-text PHI fields are masked.',
  },
  {
    value: 'view_specific_folders',
    label: 'View specific folders only',
    description: 'Restrict to a chosen subset of folders (Phase 2).',
  },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface NewExternalGrantDialogProps {
  binderId: string;
  /** Punch-list DT2-BLIND (Spike F fix) — the blinded-reviewer checkbox
   *  only appears for Single-/Double-Blind studies. */
  studyId?: string;
  open: boolean;
  onClose: () => void;
  onCreated?: (res: CreateExternalGrantResponse) => void;
}

export function NewExternalGrantDialog({
  binderId,
  studyId,
  open,
  onClose,
  onCreated,
}: NewExternalGrantDialogProps) {
  const qc = useQueryClient();

  const studiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
    enabled: open && !!studyId,
  });
  const studyBlindingType = studiesQ.data?.rows.find((s) => s.study_id === studyId)?.blinding_type;
  const showBlindingToggle = isBlindedStudyType(studyBlindingType);

  const bindersQ = useQuery({
    queryKey: ['binder', 'list'],
    queryFn: binder.list,
    staleTime: 60_000,
    enabled: open && showBlindingToggle,
  });
  const isUnblindedBinder =
    binderScope(bindersQ.data?.binders.find((b) => b.binder_id === binderId)) === 'unblinded';

  // ── Form state ──────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RemoteAccessRole>('sponsor_monitor');
  const [scope, setScope] = useState<RemoteAccessScope>('view_pii_redacted');
  const [validFrom, setValidFrom] = useState<string>(todayIso());
  // Client (2026-09-22): default the access window to a single day, not a
  // month — a monitor/inspector visit is time-boxed. Reviewer can extend.
  const [validTo, setValidTo] = useState<string>(plusDaysIso(1));
  const [blinded, setBlinded] = useState(false);

  // ── Post-success state ──────────────────────────────────────────────
  const [result, setResult] = useState<CreateExternalGrantResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset whenever the dialog reopens.
  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setRole('sponsor_monitor');
      setScope('view_pii_redacted');
      setValidFrom(todayIso());
      setValidTo(plusDaysIso(1));
      setBlinded(false);
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const isEmailValid = EMAIL_REGEX.test(trimmedEmail);
  const isDateRangeValid = useMemo(
    () => !!validFrom && !!validTo && validFrom <= validTo,
    [validFrom, validTo],
  );
  const isValid = trimmedName.length > 0 && isEmailValid && isDateRangeValid;

  const create = useMutation({
    mutationFn: () =>
      externalGrants.create(binderId, {
        granted_to_name: trimmedName,
        granted_to_email: trimmedEmail,
        granted_to_role: role,
        scope,
        valid_from_iso: new Date(validFrom).toISOString(),
        valid_to_iso: new Date(validTo).toISOString(),
        blinded: showBlindingToggle ? blinded && !isUnblindedBinder : undefined,
      }),
    onSuccess: (res) => {
      toast.success(`Magic link sent to ${trimmedEmail}`);
      setResult(res);
      qc.invalidateQueries({ queryKey: ['external-grants', binderId] });
      onCreated?.(res);
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to create grant'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || create.isPending || result) return;
    create.mutate();
  }

  async function copyLink() {
    if (!result?.magic_link_url) return;
    try {
      await navigator.clipboard.writeText(result.magic_link_url);
      setCopied(true);
      toast.success('Magic link copied to clipboard');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link — copy it manually.');
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !create.isPending) onClose();
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Grant external access</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Issue a binder-scoped magic-link session for a sponsor monitor or auditor.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-md border border-success-200 bg-success-50 p-3 text-xs text-success-700">
              Grant created. The reviewer has been emailed a one-time link at{' '}
              <span className="font-medium">{trimmedEmail}</span>.
            </div>
            <div>
              <label className="label text-xs" htmlFor="magic-link-url">
                Magic link
              </label>
              <div className="flex items-stretch gap-2">
                <input
                  id="magic-link-url"
                  className="input flex-1 font-mono text-[11px]"
                  value={result.magic_link_url}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button type="button" className="btn-ghost text-xs" onClick={copyLink}>
                  {copied ? (
                    <Check className="h-4 w-4 text-success-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-1 text-[11px] text-ink-muted">
                Forward this manually only if the reviewer reports the email did not arrive. The
                link expires when the grant does.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" className="btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="label text-xs" htmlFor="g-name">
                  Reviewer name *
                </label>
                <input
                  id="g-name"
                  className="input"
                  maxLength={200}
                  value={name}
                  placeholder="Jane Smith"
                  disabled={create.isPending}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label text-xs" htmlFor="g-email">
                  Reviewer email *
                </label>
                <input
                  id="g-email"
                  className="input"
                  type="email"
                  maxLength={320}
                  value={email}
                  placeholder="jane.smith@sponsor.org"
                  disabled={create.isPending}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {trimmedEmail.length > 0 && !isEmailValid && (
                  <p className="mt-1 text-[11px] text-danger-700">Enter a valid email address.</p>
                )}
              </div>
            </div>

            <div>
              <label className="label text-xs">Role *</label>
              <div className="flex flex-wrap gap-2">
                <label className="flex flex-1 cursor-pointer items-start gap-2 rounded border border-surface-border px-3 py-2 text-xs hover:bg-surface-muted">
                  <input
                    type="radio"
                    name="external-grant-role"
                    value="sponsor_monitor"
                    checked={role === 'sponsor_monitor'}
                    disabled={create.isPending}
                    onChange={() => setRole('sponsor_monitor')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-medium text-ink-primary">Sponsor monitor</div>
                    <div className="text-[11px] text-ink-muted">
                      Routine source-document verification visits.
                    </div>
                  </div>
                </label>
                <label className="flex flex-1 cursor-pointer items-start gap-2 rounded border border-surface-border px-3 py-2 text-xs hover:bg-surface-muted">
                  <input
                    type="radio"
                    name="external-grant-role"
                    value="auditor"
                    checked={role === 'auditor'}
                    disabled={create.isPending}
                    onChange={() => setRole('auditor')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-medium text-ink-primary">Auditor</div>
                    <div className="text-[11px] text-ink-muted">
                      Independent / regulatory audit reviewer.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="label text-xs" htmlFor="g-scope">
                Scope *
              </label>
              <select
                id="g-scope"
                className="input"
                value={scope}
                disabled={create.isPending}
                onChange={(e) => setScope(e.target.value as RemoteAccessScope)}
              >
                {SCOPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-ink-muted">
                {SCOPE_OPTIONS.find((o) => o.value === scope)?.description}
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="label text-xs" htmlFor="g-valid-from">
                  Valid from *
                </label>
                <input
                  id="g-valid-from"
                  className="input"
                  type="date"
                  value={validFrom}
                  disabled={create.isPending}
                  onChange={(e) => setValidFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label text-xs" htmlFor="g-valid-to">
                  Valid to *
                </label>
                <input
                  id="g-valid-to"
                  className="input"
                  type="date"
                  value={validTo}
                  min={validFrom}
                  disabled={create.isPending}
                  onChange={(e) => setValidTo(e.target.value)}
                  required
                />
                {!isDateRangeValid && validFrom && validTo && (
                  <p className="mt-1 text-[11px] text-danger-700">
                    "Valid to" must be on or after "Valid from".
                  </p>
                )}
              </div>
            </div>

            {showBlindingToggle && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 rounded-md border border-surface-border bg-surface-muted/30 px-3 py-2 text-xs text-ink-secondary">
                  <input
                    type="checkbox"
                    checked={blinded && !isUnblindedBinder}
                    disabled={create.isPending || isUnblindedBinder}
                    onChange={(e) => setBlinded(e.target.checked)}
                  />
                  This reviewer is <b>blinded</b> on this study — they'll see a redaction notice
                  instead of the binder, exactly like an internal blinded user.
                </label>
                {isUnblindedBinder && (
                  <p className="text-[11px] text-ink-muted">
                    Unblinded binders can only be shared with unblinded reviewers.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={onClose}
                disabled={create.isPending}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={!isValid || create.isPending}>
                {create.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send magic link
              </button>
            </div>

            <p className="flex items-start gap-1.5 text-[11px] text-ink-muted">
              <LinkIcon className="mt-0.5 h-3 w-3 shrink-0" />
              The reviewer will receive a one-time-use link by email; verifying an OTP unlocks a
              scoped, read-only binder session.
            </p>
          </>
        )}
      </form>
    </div>
  );
}

export default NewExternalGrantDialog;
